// src/features/dashboard/components/Heatmap.tsx

import {
  HOURS,
} from '../mocks/heatmap.mock';

import {
  getColor,
} from '../utils/heatmap.utils';

import {
  useHeatmap,
} from '../hooks/useHeatmap';

import {
  useManagerDate,
  formatManagerDate,
} from '../context/ManagerDateContext';


/* ============================================================
   PROPS
============================================================ */

type Props = {
  managerEmployeeCode: string;
};


/* ============================================================
   COMPONENT
============================================================ */

const Heatmap = ({
  managerEmployeeCode,
}: Props) => {

  /* ==========================================================
     MANAGER DATE
     
     This is used for displaying the selected range.
     
     useHeatmap() independently uses the same context
     for making the API call.
  ========================================================== */

  const {
    fromDate,
    toDate,
  } = useManagerDate();


  /* ==========================================================
     FORMAT SELECTED DATES
  ========================================================== */

  const fromDateString =
    formatManagerDate(
      fromDate,
    );

  const toDateString =
    formatManagerDate(
      toDate,
    );


  /* ==========================================================
     HEATMAP API
     
     IMPORTANT:
     
     Only employee code is passed.
     
     Dates are automatically read inside useHeatmap()
     from ManagerDateContext.
  ========================================================== */

  const {
    rows,
    loading,
    error,
  } = useHeatmap({
    managerEmployeeCode,
  });


  /* ==========================================================
     RENDER
  ========================================================== */

  return (
    <div
      className="
        min-w-0
        rounded-[18px]
        border
        border-[#E2E8F0]
        bg-white
        p-5
        shadow-sm
      "
    >

      {/* ======================================================
          HEADER
      ====================================================== */}

      <div
        className="
          flex
          min-w-0
          flex-wrap
          items-center
          gap-3
        "
      >

        <h2
          className="
            text-[18px]
            font-semibold
            text-[#002B5B]
          "
        >
          Team Activity Heatmap
        </h2>

        <div
          className="
            rounded-full
            bg-[#EDF4FF]
            px-3
            py-1
            text-[11px]
            font-medium
            text-[#2563EB]
          "
        >
          {fromDateString}
          {' - '}
          {toDateString}
        </div>

        <div
          className="
            rounded-full
            bg-[#F1F5F9]
            px-3
            py-1
            text-[11px]
            font-medium
            text-[#64748B]
          "
        >
          By Hour
        </div>

      </div>


      {/* ======================================================
          DESCRIPTION
      ====================================================== */}

      <div
        className="
          mt-4
          text-[13px]
          text-[#64748B]
        "
      >
        Avg active minutes per associate × hour

        <span className="mx-2">
          ·
        </span>

        Darker = more active

        <span className="mx-2">
          ·
        </span>

        Hover over any cell for details
      </div>


      {/* ======================================================
          ERROR
      ====================================================== */}

      {error && !loading && (
        <div
          className="
            mt-4
            rounded-lg
            border
            border-[#FECACA]
            bg-[#FEF2F2]
            px-4
            py-3
            text-[13px]
            text-[#B91C1C]
          "
        >
          {error}
        </div>
      )}


      {/* ======================================================
          LOADING
      ====================================================== */}

      {loading && (
        <div
          className="
            mt-4
            flex
            min-h-[220px]
            items-center
            justify-center
            text-sm
            text-[#64748B]
          "
        >
          Loading heatmap...
        </div>
      )}


      {/* ======================================================
          DATA
      ====================================================== */}

      {!loading && !error && (
        <>

          {/* ==================================================
              SHIFT HEADERS
          ================================================== */}

          <div
            className="
              mt-5
              ml-[90px]
              grid
              grid-cols-3
              gap-2
            "
          >

            <div
              className="
                rounded
                bg-[#4338CA]
                py-2
                text-center
                text-[12px]
                font-bold
                text-white
              "
            >
              NIGHT SHIFT · 22 → 06
            </div>

            <div
              className="
                rounded
                bg-[#F59E0B]
                py-2
                text-center
                text-[12px]
                font-bold
                text-white
              "
            >
              MORNING SHIFT · 06 → 14
            </div>

            <div
              className="
                rounded
                bg-[#8B5CF6]
                py-2
                text-center
                text-[12px]
                font-bold
                text-white
              "
            >
              EVENING SHIFT · 14 → 22
            </div>

          </div>


          {/* ==================================================
              HOURS
          ================================================== */}

          <div
            className="
              mt-4
              flex
              min-w-0
            "
          >

            <div
              className="
                w-[90px]
                shrink-0
              "
            />

            <div
              className="
                grid
                min-w-0
                flex-1
                gap-[8px]
              "
              style={{
                gridTemplateColumns:
                  'repeat(24, minmax(0, 1fr))',
              }}
            >

              {HOURS.map(
                (
                  hour,
                ) => (

                  <div
                    key={hour}
                    className="
                      min-w-0
                      text-center
                      text-[11px]
                      font-semibold
                      text-[#475569]
                    "
                  >
                    {hour}
                  </div>

                ),
              )}

            </div>

          </div>


          {/* ==================================================
              EMPLOYEE ROWS
          ================================================== */}

          <div
            className="
              mt-4
              space-y-3
            "
          >

            {rows.length === 0 ? (

              <div
                className="
                  rounded-lg
                  border
                  border-[#E5E7EB]
                  bg-[#F8FAFC]
                  px-4
                  py-8
                  text-center
                  text-[13px]
                  text-[#64748B]
                "
              >
                No heatmap data available for the selected date.
              </div>

            ) : (

              rows.map(
                (
                  row,
                ) => (

                  <div
                    key={row.name}
                    className="
                      flex
                      min-w-0
                      items-center
                    "
                  >

                    {/* ========================================
                        EMPLOYEE INFO
                    ======================================== */}

                    <div
                      className="
                        w-[90px]
                        shrink-0
                        pr-3
                      "
                    >

                      <div
                        className="
                          truncate
                          text-[13px]
                          font-semibold
                          text-[#334155]
                        "
                        title={row.name}
                      >
                        {row.name}
                      </div>

                      <div
                        className="
                          text-[11px]
                          leading-[15px]
                          text-[#64748B]
                        "
                      >

                        {row.hc}
                        {' '}
                        HC

                        <br />

                        {row.shift}

                      </div>

                    </div>


                    {/* ========================================
                        HEATMAP GRID
                    ======================================== */}

                    <div
                      className="
                        grid
                        min-w-0
                        flex-1
                        gap-[8px]
                      "
                      style={{
                        gridTemplateColumns:
                          'repeat(24, minmax(0, 1fr))',
                      }}
                    >

                      {row.activity.map(
                        (
                          value,
                          index,
                        ) => (

                          <div
                            key={index}
                            title={
                              `${row.name} | ${HOURS[index]}:00`
                            }
                            className="
                              h-[32px]
                              min-w-0
                              rounded-[5px]
                              border
                              border-[#F1F5F9]
                              transition-all
                              duration-200
                              hover:scale-105
                            "
                            style={{
                              backgroundColor:
                                getColor(
                                  value,
                                ),
                            }}
                          />

                        ),
                      )}

                    </div>

                  </div>

                ),
              )

            )}

          </div>


          {/* ==================================================
              LEGEND + PEAK TIMINGS
          ================================================== */}

          <div
            className="
              mt-6
              flex
              flex-wrap
              items-center
              justify-between
              gap-4
            "
          >

            {/* ================================================
                LEGEND
            ================================================ */}

            <div
              className="
                flex
                flex-wrap
                items-center
                gap-2
              "
            >

              <span
                className="
                  text-xs
                  text-[#64748B]
                "
              >
                Less active
              </span>

              {[
                0,
                20,
                40,
                60,
                80,
                100,
              ].map(
                (
                  value,
                ) => (

                  <div
                    key={value}
                    className="
                      h-3
                      w-3
                      rounded
                    "
                    style={{
                      backgroundColor:
                        getColor(
                          value,
                        ),
                    }}
                  />

                ),
              )}

              <span
                className="
                  text-xs
                  text-[#64748B]
                "
              >
                More active
              </span>

            </div>


            {/* ================================================
                PEAK TIMINGS
            ================================================ */}

            <div
              className="
                flex
                flex-wrap
                gap-5
                text-xs
                text-[#64748B]
              "
            >

              <div>
                Night peak:{' '}
                <span
                  className="
                    font-semibold
                    text-[#475569]
                  "
                >
                  02–04 IST
                </span>
              </div>

              <div>
                Morning peak:{' '}
                <span
                  className="
                    font-semibold
                    text-[#475569]
                  "
                >
                  10–12 IST
                </span>
              </div>

              <div>
                Evening peak:{' '}
                <span
                  className="
                    font-semibold
                    text-[#475569]
                  "
                >
                  16–18 IST
                </span>
              </div>

            </div>

          </div>

        </>
      )}

    </div>
  );
};


export default Heatmap;